import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Target, TrendingUp, Calendar, Award, Download } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { exportToCSV, formatReportData } from "@/lib/export-utils";

const DashboardStats = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalModules: 0,
    completedModules: 0,
    totalStudyTime: 0,
    currentStreak: 0,
    completionRate: 0,
    weeklyActivity: [] as { day: string; minutes: number }[]
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: progress } = await supabase
        .from("course_progress")
        .select("*")
        .eq("user_id", user.id);

      if (progress) {
        const completed = progress.filter(p => p.completed).length;
        const total = progress.length;
        const completionRate = total > 0 ? (completed / total) * 100 : 0;

        setStats({
          totalModules: total,
          completedModules: completed,
          totalStudyTime: completed * 45, // Estimate 45 min per module
          currentStreak: calculateStreak(progress),
          completionRate,
          weeklyActivity: generateWeeklyActivity(progress)
        });
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStreak = (progress: any[]) => {
    const completedDates = progress
      .filter(p => p.completed_at)
      .map(p => new Date(p.completed_at).toDateString())
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let streak = 0;
    const today = new Date().toDateString();
    
    for (let i = 0; i < completedDates.length; i++) {
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      
      if (completedDates[i] === expectedDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const generateWeeklyActivity = (progress: any[]) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const weekData = days.map(day => ({ day, minutes: 0 }));
    
    progress.forEach(p => {
      if (p.completed_at) {
        const date = new Date(p.completed_at);
        const dayIndex = (date.getDay() + 6) % 7; // Adjust to start from Monday
        weekData[dayIndex].minutes += 45;
      }
    });
    
    return weekData;
  };

  const handleDownloadStats = () => {
    const formattedData = formatReportData(stats);
    const filename = `study_stats_${new Date().toISOString().split('T')[0]}`;
    exportToCSV(formattedData, filename);
    
    toast({
      title: "Statistics Downloaded",
      description: "Your study statistics have been downloaded successfully.",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold">Study Statistics</h1>
            <p className="text-muted-foreground">Track your learning progress and habits</p>
          </div>
          <Button onClick={handleDownloadStats} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Download Stats
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Study Time</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.floor(stats.totalStudyTime / 60)}h {stats.totalStudyTime % 60}m</div>
              <p className="text-xs text-muted-foreground">Across all modules</p>
            </CardContent>
          </Card>

          <Card className="hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Streak</CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.currentStreak} days</div>
              <p className="text-xs text-muted-foreground">Keep it going!</p>
            </CardContent>
          </Card>

          <Card className="hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Modules Completed</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.completedModules}/{stats.totalModules}</div>
              <Progress value={stats.completionRate} className="mt-2" />
            </CardContent>
          </Card>

          <Card className="hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.completionRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground">Of enrolled modules</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Weekly Activity</CardTitle>
            <CardDescription>Your study time over the past week</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2 h-48">
              {stats.weeklyActivity.map((day, i) => {
                const maxMinutes = Math.max(...stats.weeklyActivity.map(d => d.minutes), 1);
                const height = (day.minutes / maxMinutes) * 100;
                
                return (
                  <div key={i} className="flex flex-col items-center gap-2 flex-1">
                    <div className="w-full bg-primary/20 rounded-t-lg relative group">
                      <div
                        className="w-full bg-primary rounded-t-lg transition-all duration-500 hover:opacity-80"
                        style={{ height: `${height}%`, minHeight: day.minutes > 0 ? '8px' : '0' }}
                      />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-popover text-popover-foreground px-2 py-1 rounded text-xs whitespace-nowrap">
                        {day.minutes} min
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{day.day}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardStats;
